/*
 * Copyright (c) 2018 teamapps.org (see code comments for author's name)
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import {TeamAppsConnection, TeamAppsConnectionListener} from "../shared/TeamAppsConnection";
import {ReconnectingCompressingWebSocketConnection} from "./ReconnectingWebSocketConnection";
import {UiClientInfoConfig} from "../generated/UiClientInfoConfig";
import {INITConfig} from "../generated/INITConfig";
import {INIT_NOK_Reason, INIT_NOKConfig} from "../generated/INIT_NOKConfig";
import {REINIT_NOK_Reason, REINIT_NOKConfig} from "../generated/REINIT_NOKConfig";
import {REINITConfig} from "../generated/REINITConfig";
import {CMD_REQUESTConfig} from "../generated/CMD_REQUESTConfig";
import {INIT_OKConfig} from "../generated/INIT_OKConfig";
import {REINIT_OKConfig} from "../generated/REINIT_OKConfig";
import {MULTI_CMDConfig} from "../generated/MULTI_CMDConfig";
import {EVENTConfig} from "../generated/EVENTConfig";
import {SERVER_ERROR_Reason, SERVER_ERRORConfig} from "../generated/SERVER_ERRORConfig";
import {CMD} from "./CMD";

/**
 * @author Yann Massard (yamass@gmail.com)
 */

interface EventWrapper {
	id: number,
	redundancyKey?: string,
	event: any
}

enum TeamAppsProtocolStatus {
	INITIALIZING,
	REINITIALIZING,
	ERROR,
	ESTABLISHED
}

export class TeamAppsConnectionImpl implements TeamAppsConnection {

	private static readonly REDUNDANCY_LOCK_TIMEOUT_MILLIS = 200;
	private static readonly SENT_EVENTS_MIN_BUFFER_SIZE = 500;
	private static readonly SENT_EVENTS_MAX_BUFFER_SIZE = 1000;
	private static readonly KEEPALIVE_INTERVAL = 25000;
	private static readonly MIN_REQUESTED_COMMANDS = 5;
	private static readonly MAX_REQUESTED_COMMANDS = 20;

	private protocolStatus: TeamAppsProtocolStatus;

	private connection: ReconnectingCompressingWebSocketConnection;

	/**
	 * There are two cases in which the queue gets filled:
	 *  # When the WebSocket connection is interrupted. In this case the queue will be be flushed once the connection is re-established.
	 *  # When there is a redundancy lock preventing events from being sent. In this case the queue will be flushed once the lock timeout fires.
	 */
	private eventQueue: EventWrapper[] = [];
	private sentEventsBuffer: EventWrapper[] = [];

	/**
	 * Dictionary of redundancy lock timeouts (specifying the unix timestamp after which the lock is obsolete)
	 */
	private redundancyKeyLockTimeouts: { [key: string]: number } = {};
	private waitingForRedundancyLock: boolean = false;

	private eventIdCounter = 1;

	private maxRequestedCommandId = 0;
	private lastReceivedCommandId: number;

	constructor(url: string, private sessionId: string, clientInfo: UiClientInfoConfig, commandHandler: TeamAppsConnectionListener) {
		if (sessionId == null) {
			throw "sessionId may not be null!!";
		}
		this.connection = new ReconnectingCompressingWebSocketConnection(url, {
			onConnected: () => {
				this.protocolStatus = TeamAppsProtocolStatus.INITIALIZING;
				this.connection.send({
					_type: "INIT",
					sessionId,
					clientInfo,
					maxRequestedCommandId: TeamAppsConnectionImpl.MAX_REQUESTED_COMMANDS
				} as INITConfig)
			},
			onMessage: (message) => {
				if (TeamAppsConnectionImpl.isMULTI_CMD(message)) {
					let cmds = message.cmds as CMD[];
					commandHandler.onCommands(cmds.map(cmd => cmd.uiCommand), false);
					this.lastReceivedCommandId = cmds[cmds.length - 1].id;
					this.ensureEnoughCommandsRequested();
				} else if (TeamAppsConnectionImpl.isINIT_OK(message)) {
					this.protocolStatus = TeamAppsProtocolStatus.ESTABLISHED;
					this.log("Connection accepted.");
					this.flushEvents();
					commandHandler.onConnectionInitialized();
				} else if (TeamAppsConnectionImpl.isINIT_NOK(message)) {
					this.protocolStatus = TeamAppsProtocolStatus.ERROR;
					this.log("Connection refused. Reason: " + INIT_NOK_Reason[message.reason]);
					commandHandler.onConnectionBroken(message.reason);
					// do NOT close the connection here, since the server might send some additional commands to redirect or re-initialize the UI.
				} else if (TeamAppsConnectionImpl.isREINIT_OK(message)) {
					this.protocolStatus = TeamAppsProtocolStatus.ESTABLISHED;
					this.log("Reconnect accepted.");

					let lastReceivedEventIndex: number;
					for (let i = 0; i < this.sentEventsBuffer.length; i++) {
						if (this.sentEventsBuffer[i].id === message.lastReceivedEventId) {
							lastReceivedEventIndex = i;
						}
					}
					let eventsToBeResent = this.sentEventsBuffer.slice(lastReceivedEventIndex + 1, this.sentEventsBuffer.length);
					this.eventQueue.splice(0, 0, ...eventsToBeResent);
					this.sentEventsBuffer = [];

					this.flushEvents();
				} else if (TeamAppsConnectionImpl.isREINIT_NOK(message)) {
					this.protocolStatus = TeamAppsProtocolStatus.ERROR;
					this.log("Reconnect refused. Reason: " + REINIT_NOK_Reason[message.reason]);
					commandHandler.onConnectionBroken(message.reason);
					// do NOT close the connection here, since the server might send some additional commands to redirect or re-initialize the UI.
				} else if (TeamAppsConnectionImpl.isSERVER_ERROR(message)) {
					this.protocolStatus = TeamAppsProtocolStatus.ERROR;
					this.log("Error reported by server: " + SERVER_ERROR_Reason[message.reason] + ": " + message.message);
					commandHandler.onConnectionBroken(message.reason, message.message);
				} else {
					this.log(`ERROR: unknown message type: ${message._type}`)
				}
			},
			onConnectionLost: () => {
				// do nothing...
			},
			onReconnected: () => {
				this.protocolStatus = TeamAppsProtocolStatus.REINITIALIZING;
				this.connection.send({
					_type: "REINIT",
					sessionId,
					lastReceivedCommandId: this.lastReceivedCommandId || -1,
					maxRequestedCommandId: this.lastReceivedCommandId + TeamAppsConnectionImpl.MAX_REQUESTED_COMMANDS
				} as REINITConfig)
			}
		});
		self.setInterval(() => {
			if (this.isConnected()) {
				this.connection.send({
					_type: "KEEPALIVE",
					sessionId: sessionId
				})
			}
		}, TeamAppsConnectionImpl.KEEPALIVE_INTERVAL)
	}

	private ensureEnoughCommandsRequested() {
		if (this.maxRequestedCommandId - this.lastReceivedCommandId <= TeamAppsConnectionImpl.MIN_REQUESTED_COMMANDS) {
			try {
				let maxRequestedCommandId = this.lastReceivedCommandId + TeamAppsConnectionImpl.MAX_REQUESTED_COMMANDS;
				this.connection.send({
					_type: "CMD_REQUEST",
					sessionId: this.sessionId,
					maxRequestedCommandId: maxRequestedCommandId
				} as CMD_REQUESTConfig);
				this.maxRequestedCommandId = maxRequestedCommandId;
			} catch (e) {
				this.log(`Could not send CMD_REQUEST: ${e}.`);
			}
		}
	}

	private static isINIT_OK(message: any): message is INIT_OKConfig & {tscompilerbugworkaround:number} {
		return message._type === 'INIT_OK';
	}

	private static isINIT_NOK(message: any): message is INIT_NOKConfig {
		return message._type === 'INIT_NOK';
	}

	private static isREINIT_OK(message: any): message is REINIT_OKConfig {
		return message._type === 'REINIT_OK';
	}

	private static isREINIT_NOK(message: any): message is REINIT_NOKConfig {
		return message._type === 'REINIT_NOK';
	}

	private static isMULTI_CMD(message: any): message is MULTI_CMDConfig {
		return message._type === 'MULTI_CMD';
	}

	private static isSERVER_ERROR(message: any): message is SERVER_ERRORConfig {
		return message._type === 'SERVER_ERROR';
	}

	sendEvent(event: any, redundancyKey?: string) {
		let eventWrapper: EventWrapper = {
			id: this.eventIdCounter++,
			event,
			redundancyKey
		};

		/*
		connection active? | waiting for redundancy lock? | this event's lock active? | consequence
		-----------------------------------------------------------------------------------------------------------------------
		false              | false                        | false                     | put to queue
		false              | false                        | true                      | put to queue
		false              | true                         | false                     | put to queue
		false              | true                         | true                      | put to queue
		true               | true                         | false                     | put to queue
		true               | true                         | true                      | put to queue
		true               | false                        | true                      | put to queue and init lockReleaseTimer
		true               | false                        | false                     | send and set redundancyKeyLockTimeout
		 */
		if (!this.isConnected() || this.waitingForRedundancyLock) {
			this.eventQueue.push(eventWrapper);
		} else {
			let millisecondsTillRelease = this.getMillisecondsTillReleaseOfRedundancyLock(eventWrapper.redundancyKey);
			if (millisecondsTillRelease > 0) {
				this.eventQueue.push(eventWrapper);
				this.waitingForRedundancyLock = true;
				setTimeout(() => {
					this.waitingForRedundancyLock = false;
					this.flushEvents();
				}, millisecondsTillRelease);
			} else {
				this.doSendEvent(eventWrapper);
				this.redundancyKeyLockTimeouts[eventWrapper.redundancyKey] = new Date().getTime() + TeamAppsConnectionImpl.REDUNDANCY_LOCK_TIMEOUT_MILLIS;
			}
		}
	}

	private isConnected() {
		return this.connection.isConnected() && this.protocolStatus === TeamAppsProtocolStatus.ESTABLISHED;
	}

	private getMillisecondsTillReleaseOfRedundancyLock(redundancyKey: string) {
		if (redundancyKey != null) {
			let blockingEndTime = this.redundancyKeyLockTimeouts[redundancyKey];
			return blockingEndTime - new Date().getTime();
		} else {
			return -1;
		}
	}

	private flushEvents() {
		// - check which events can get sent with regard to their redundancy locks
		// - with this list in hand: perform redundancy filtering
		// - send filtered list

		let now = new Date().getTime();

		let eventsToBeSent = [];
		for (let i = 0; i < this.eventQueue.length; i++) {
			let eventWrapper = this.eventQueue[i];
			if (this.getMillisecondsTillReleaseOfRedundancyLock(eventWrapper.redundancyKey) <= 0) {
				eventsToBeSent.push(eventWrapper);
			} else {
				break;
			}
		}

		this.eventQueue.splice(0, eventsToBeSent.length);

		this.dropRedundantEvents(eventsToBeSent);

		for (let i = 0; i < eventsToBeSent.length; i++) {
			this.doSendEvent(eventsToBeSent[i]);
		}
	}

	private dropRedundantEvents(events: EventWrapper[]) {
		const seenRedundancyKeys: { [key: string]: string } = {};
		return events
			.reverse()
			.filter((e) => {
				if (e.redundancyKey) {
					if (seenRedundancyKeys[e.redundancyKey]) {
						this.log("Dopping redundant event of type " + (e.event as any)._type);
						return false;
					} else {
						seenRedundancyKeys[e.redundancyKey] = e.redundancyKey;
						return true;
					}
				} else {
					return true;
				}
			}).reverse();
	};

	private doSendEvent(eventWrapper: EventWrapper) {
		this.sentEventsBuffer.push(eventWrapper);
		if (this.sentEventsBuffer.length > TeamAppsConnectionImpl.SENT_EVENTS_MAX_BUFFER_SIZE) {
			this.sentEventsBuffer.splice(0, TeamAppsConnectionImpl.SENT_EVENTS_MAX_BUFFER_SIZE - TeamAppsConnectionImpl.SENT_EVENTS_MIN_BUFFER_SIZE);
		}
		this.connection.send({
			_type: "EVENT",
			sessionId: this.sessionId,
			id: eventWrapper.id,
			uiEvent: eventWrapper.event
		} as EVENTConfig);
	}

	private log(...message: string[]) {
		console.log("TeamAppsConnection: " + message);
	}
}
